import type { AccountInfo, IPublicClientApplication } from "@azure/msal-browser";
import { setAuthToken } from "./ApiHelper";
import { acquirePortalIdToken, clearPortalRedirectAttempt, PortalIdToken } from "./portalIdToken";

/** What Home does next: "ok" signed in; "redirect" the page is going through Microsoft; "refused"
 *  the Unauthorized screen; "failed" Try again. */
export type PortalSignInOutcome = "ok" | "redirect" | "refused" | "failed";

type MsalForSignIn = Pick<IPublicClientApplication, "acquireTokenSilent" | "acquireTokenRedirect">;

const send = async (idToken: PortalIdToken): Promise<PortalSignInOutcome | "expired"> => {
    if (idToken.kind === "redirect") return "redirect";
    if (idToken.kind !== "token") return "failed";
    return setAuthToken(idToken.idToken);
};

/**
 * Portal sign-in (spec §1.2): the Entra ID token to GetAuthToken. An ID token the API found expired
 * (a client clock well behind Microsoft's) is renewed once with forceRefresh and sent again; still
 * expired counts as a refusal (audit 2026-10-19). Never signs the user out of Microsoft. A sign-in
 * the API accepted ends the tab's redirect attempt; nothing earlier does (review 2026-10-20).
 */
export const signInToPortal = async (instance: MsalForSignIn, account: AccountInfo | undefined): Promise<PortalSignInOutcome> => {
    const first = await send(await acquirePortalIdToken(instance, account));
    const outcome: PortalSignInOutcome = first !== "expired"
        ? first
        : await send(await acquirePortalIdToken(instance, account, Date.now, { forceRefresh: true })).then(second => second === "expired" ? "refused" : second);
    // Only a sign-in the API accepted ends the tab's redirect attempt (review 2026-10-20).
    if (outcome === "ok") {
        clearPortalRedirectAttempt();
    }
    return outcome;
};

/** "Try again": the next sign-in may go through Microsoft once more, then the page starts over. */
export const retryPortalSignIn = (reload: () => void = () => window.location.reload()): void => {
    clearPortalRedirectAttempt();
    reload();
};
